import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

export interface ArmonyUser {
  id: string;
  name: string;
  email: string;
  role: "admin" | "customer" | "staff";
  cedula?: string | null;
  phone?: string | null;
  points: number;
  status: string;
  inactive_reason?: string | null;
  approval_status?: string;
  approval_reason?: string | null;
  is_verified_client: boolean;
  birthdate?: string | null;
  permissions: string[];
  tier: string;
  tier_color: string;
  tier_multiplier: number;
  next_tier?: string | null;
  next_tier_points?: number | null;
  tier_points?: number;
  tier_locked?: boolean;
  tier_valid_until?: string;
  points_to_next: number;
  progress: number;
  plan?: string | null;
  plan_interval?: string | null;
  subscription_status?: string | null;
  subscription_current_period_end?: string | null;
  subscription_cancel_at?: string | null;
  subscription_cancel_requested_at?: string | null;
}

interface AuthCtx {
  user: ArmonyUser | null | undefined; // undefined = checking
  login: (email: string, password: string) => Promise<ArmonyUser>;
  register: (payload: any) => Promise<ArmonyUser>;
  logout: () => void;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({} as AuthCtx);
export const useAuth = () => useContext(Ctx);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<ArmonyUser | null | undefined>(undefined);

  const loadMe = async () => {
    const token = localStorage.getItem("armony_token");
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("armony_token");
      setUser(null);
    }
  };

  useEffect(() => {
    loadMe();
  }, []);

  // Permisos cambiados durante la sesión: recargar el usuario tras un 403.
  useEffect(() => {
    let pending = false;
    const onForbidden = () => {
      if (pending) return;
      pending = true;
      api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => {}).finally(() => { pending = false; });
    };
    window.addEventListener("armony:forbidden", onForbidden);
    return () => window.removeEventListener("armony:forbidden", onForbidden);
  }, []);

  const login = async (email: string, password: string) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("armony_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload: any) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("armony_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("armony_token");
    setUser(null);
  };

  const refresh = async () => {
    await loadMe();
  };

  return <Ctx.Provider value={{ user, login, register, logout, refresh }}>{children}</Ctx.Provider>;
};
